/**
 * Hooks for admin challenge management with skill filtering.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { apiGet, apiDelete } from "../lib/api";
import type { DisciplineSlug, SkillKey, ContentReviewStatus } from "@jumpbean/shared";

// ----- Types -----

export interface ChallengeListItem {
  id: string;
  levelId: string;
  levelNumber: number;
  levelName: string;
  disciplineId: string;
  disciplineSlug: string;
  disciplineName: string;
  challengeNumber: number;
  type: string;
  content: Record<string, unknown>;
  difficulty: number;
  isActive: boolean;
  skillTags: string[];
  aiMetadata: Record<string, unknown> | null;
  reviewStatus: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
}

export interface ChallengeListFilters {
  discipline?: DisciplineSlug;
  level?: number;
  type?: string;
  reviewStatus?: ContentReviewStatus;
  skillTags?: string[];
  isActive?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}

interface ChallengeListResponse {
  success: boolean;
  data: {
    challenges: ChallengeListItem[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  };
}

// ----- useAdminChallenges Hook -----

export function useAdminChallenges(initialFilters: ChallengeListFilters = {}) {
  const [challenges, setChallenges] = useState<ChallengeListItem[]>([]);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ChallengeListFilters>({
    limit: 50,
    offset: 0,
    ...initialFilters,
  });

  // Debounce search
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchChallenges = useCallback(
    async (currentFilters: ChallengeListFilters) => {
      setIsLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();

        if (currentFilters.discipline) {
          params.set("discipline", currentFilters.discipline);
        }
        if (currentFilters.level !== undefined) {
          params.set("level", String(currentFilters.level));
        }
        if (currentFilters.type) {
          params.set("type", currentFilters.type);
        }
        if (currentFilters.reviewStatus) {
          params.set("reviewStatus", currentFilters.reviewStatus);
        }
        if (currentFilters.skillTags && currentFilters.skillTags.length > 0) {
          params.set("skillTags", currentFilters.skillTags.join(","));
        }
        if (currentFilters.isActive !== undefined) {
          params.set("isActive", String(currentFilters.isActive));
        }
        if (currentFilters.search) {
          params.set("search", currentFilters.search);
        }
        params.set("limit", String(currentFilters.limit || 50));
        params.set("offset", String(currentFilters.offset || 0));

        const result = await apiGet<ChallengeListResponse>(
          `/api/admin/challenges?${params.toString()}`
        );

        if (result.error) {
          throw new Error(result.error.message);
        }

        if (result.data?.data) {
          setChallenges(result.data.data.challenges);
          setPagination(result.data.data.pagination);
        }
      } catch (err) {
        console.error("Failed to fetch challenges:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load challenges"
        );
        setChallenges([]);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // Initial fetch and refetch on filter changes
  useEffect(() => {
    fetchChallenges(filters);
  }, [fetchChallenges, filters]);

  // Debounced search update
  const updateSearch = useCallback((search: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search, offset: 0 }));
    }, 300);
  }, []);

  // Direct filter updates (no debounce)
  const updateFilters = useCallback(
    (newFilters: Partial<ChallengeListFilters>) => {
      setFilters((prev) => ({ ...prev, ...newFilters, offset: 0 }));
    },
    []
  );

  // Page change
  const goToPage = useCallback(
    (page: number) => {
      const offset = (page - 1) * (filters.limit || 50);
      setFilters((prev) => ({ ...prev, offset }));
    },
    [filters.limit]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Calculate current page from offset
  const currentPage = Math.floor((filters.offset || 0) / (filters.limit || 50)) + 1;
  const totalPages = Math.ceil(pagination.total / (filters.limit || 50));

  // Delete challenge
  const deleteChallenge = useCallback(
    async (challengeId: string) => {
      try {
        const result = await apiDelete<{ success: boolean }>(
          `/api/admin/challenges/${challengeId}`
        );

        if (result.error) {
          throw new Error(result.error.message);
        }

        // Refetch to update the list
        await fetchChallenges(filters);
        return { success: true };
      } catch (err) {
        console.error("Failed to delete challenge:", err);
        return {
          success: false,
          error: err instanceof Error ? err.message : "Failed to delete challenge",
        };
      }
    },
    [fetchChallenges, filters]
  );

  return {
    challenges,
    pagination: {
      ...pagination,
      page: currentPage,
      totalPages,
    },
    isLoading,
    error,
    filters,
    updateSearch,
    updateFilters,
    goToPage,
    deleteChallenge,
    refetch: () => fetchChallenges(filters),
  };
}
